import * as React from 'react';

import { Button, type ButtonProps } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';

export interface ConfirmModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    description?: string | React.ReactNode;
    confirmText?: string;
    cancelText?: string;
    confirmVariant?: ButtonProps['variant'];
    onConfirm: () => void;
    onCancel?: () => void;
    loading?: boolean;
    disabled?: boolean;
    /**
     * When false, the modal does not auto-close on confirm. Use this when the
     * caller controls closing after an async operation (so the `loading` state
     * remains visible while the mutation is in flight).
     */
    closeOnConfirm?: boolean;
}

export function ConfirmModal({
    open,
    onOpenChange,
    title,
    description,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    confirmVariant = 'default',
    onConfirm,
    onCancel,
    loading = false,
    disabled = false,
    closeOnConfirm = true,
}: ConfirmModalProps) {
    const handleCancel = () => {
        onCancel?.();
        onOpenChange(false);
    };

    const handleConfirm = () => {
        onConfirm();
        if (closeOnConfirm) onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    {description &&
                        (typeof description === 'string' ? (
                            <DialogDescription>{description}</DialogDescription>
                        ) : (
                            <DialogDescription asChild>
                                <div>{description}</div>
                            </DialogDescription>
                        ))}
                </DialogHeader>
                <DialogFooter>
                    <Button variant="outline" onClick={handleCancel} disabled={loading}>
                        {cancelText}
                    </Button>
                    <Button
                        variant={confirmVariant}
                        onClick={handleConfirm}
                        disabled={disabled || loading}
                        className={loading ? 'cursor-wait' : ''}
                    >
                        {loading && (
                            <span
                                aria-hidden
                                className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
                            />
                        )}
                        {loading ? 'Loading...' : confirmText}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
